import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  bulkExcelCourseUpload,
  downloadExcelTemplate,
  downloadErrorReport,
} from '../../../services/bulkExcelCourseUploadService';

const MAX_SHEETS = 50;

const BulkExcelCourseUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const resetState = () => {
    setFile(null);
    setProgress(null);
    setResult(null);
    setFatalError(null);
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose?.();
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
    if (!['xlsx', 'xls']?.includes(ext)) {
      setFatalError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    if (selectedFile?.size > 20 * 1024 * 1024) {
      setFatalError('File size must be under 20 MB');
      return;
    }
    setFile(selectedFile);
    setFatalError(null);
    setResult(null);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const dropped = e?.dataTransfer?.files?.[0];
    handleFileSelect(dropped);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    setFatalError(null);
    setProgress({ phase: 'starting', sheet: '', sheetIndex: 0, totalSheets: 0 });

    try {
      const uploadResult = await bulkExcelCourseUpload(file, {
        onProgress: (p) => setProgress(p),
      });

      if (uploadResult?.fatalError) {
        setFatalError(uploadResult?.fatalError);
      } else {
        setResult(uploadResult);
        if (uploadResult?.summary?.totalRowsUploaded > 0) {
          onSuccess?.();
        }
      }
    } catch (err) {
      setFatalError(err?.message || 'An unexpected error occurred');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const progressLabel = () => {
    if (!progress) return '';
    if (progress?.phase === 'starting') return 'Preparing upload…';
    if (progress?.phase === 'validating') {
      return `Validating sheet ${progress?.sheetIndex}/${progress?.totalSheets}: "${progress?.sheet}"`;
    }
    if (progress?.phase === 'uploading') {
      return `Uploading "${progress?.sheet}" — row ${progress?.rowIndex}/${progress?.totalRows}`;
    }
    return 'Processing…';
  };

  const progressPercent = () => {
    if (!progress || !progress?.totalSheets) return 0;
    const sheetFraction = (progress?.sheetIndex - 1) / progress?.totalSheets;
    const rowFraction = progress?.totalRows
      ? (progress?.rowIndex || 0) / progress?.totalRows / progress?.totalSheets
      : 0;
    return Math.min(100, Math.round((sheetFraction + rowFraction) * 100));
  };

  const { summary, errors } = result || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="FileSpreadsheet" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Bulk Excel Course Upload</h2>
              <p className="text-xs text-muted-foreground">Each sheet = one institute · Max {MAX_SHEETS} sheets per file</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* How it works */}
          {!result && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={16} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 space-y-1">
                  <p className="font-semibold">How it works</p>
                  <ul className="text-blue-600 space-y-0.5 list-disc list-inside">
                    <li>Each sheet name must exactly match an institute name in the system</li>
                    <li>Valid rows upload even if other rows or sheets fail</li>
                    <li>Up to {MAX_SHEETS} sheets (institutes) per file</li>
                    <li>A detailed error report is shown after upload</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Template download */}
          {!result && (
            <div className="flex items-center justify-between bg-muted/40 border border-border rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <Icon name="Download" size={16} color="var(--color-muted-foreground)" />
                <div>
                  <p className="text-sm font-medium text-foreground">Download Template</p>
                  <p className="text-xs text-muted-foreground">Excel file with correct column headers and sample data</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={downloadExcelTemplate}>
                Download
              </Button>
            </div>
          )}

          {/* File drop zone */}
          {!result && (
            <div
              onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef?.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-primary bg-primary/5'
                  : file
                  ? 'border-success bg-success/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
              } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => handleFileSelect(e?.target?.files?.[0])}
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <Icon name="FileCheck2" size={36} color="var(--color-success)" />
                  <p className="font-semibold text-foreground">{file?.name}</p>
                  <p className="text-xs text-muted-foreground">{(file?.size / 1024)?.toFixed(1)} KB · Click to change</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Icon name="Upload" size={36} color="var(--color-muted-foreground)" className="opacity-50" />
                  <p className="font-medium text-foreground">Drop your Excel file here</p>
                  <p className="text-sm text-muted-foreground">or click to browse · .xlsx / .xls · Max 20 MB</p>
                </div>
              )}
            </div>
          )}

          {/* Fatal error */}
          {fatalError && (
            <div className="bg-error/10 border border-error rounded-lg p-4 flex gap-3">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-error text-sm">Upload Failed</p>
                <p className="text-sm text-error/80 mt-0.5">{fatalError}</p>
              </div>
            </div>
          )}

          {/* Progress */}
          {uploading && progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{progressLabel()}</span>
                <span className="font-medium text-foreground">{progressPercent()}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent()}%` }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {result && summary && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Institutes Processed', value: summary?.processedSheets, icon: 'Building2', color: 'text-primary bg-primary/10' },
                  { label: 'Sheets Skipped', value: summary?.skippedSheets, icon: 'SkipForward', color: 'text-warning bg-warning/10' },
                  { label: 'Courses Uploaded', value: summary?.totalRowsUploaded, icon: 'CheckCircle2', color: 'text-success bg-success/10' },
                  { label: 'Rows Failed', value: summary?.totalRowsFailed, icon: 'XCircle', color: 'text-error bg-error/10' },
                ]?.map((card) => (
                  <div key={card?.label} className="bg-card border border-border rounded-lg p-3 text-center">
                    <div className={`w-8 h-8 rounded-lg ${card?.color} flex items-center justify-center mx-auto mb-2`}>
                      <Icon name={card?.icon} size={16} />
                    </div>
                    <p className="text-xl font-bold text-foreground">{card?.value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{card?.label}</p>
                  </div>
                ))}
              </div>

              {/* Per-sheet results */}
              {summary?.sheetResults?.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-muted/40 px-4 py-2 border-b border-border">
                    <p className="text-sm font-semibold text-foreground">Sheet Results</p>
                  </div>
                  <div className="divide-y divide-border max-h-48 overflow-y-auto">
                    {summary?.sheetResults?.map((sr) => (
                      <div key={sr?.sheetName} className="flex items-center justify-between px-4 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            sr?.status === 'skipped' ? 'bg-warning' : sr?.failed > 0 ? 'bg-warning' : 'bg-success'
                          }`} />
                          <span className="text-sm font-medium text-foreground truncate">{sr?.sheetName}</span>
                          {sr?.reason && (
                            <span className="text-xs text-error bg-error/10 px-1.5 py-0.5 rounded flex-shrink-0">{sr?.reason}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 text-xs ml-3">
                          {sr?.status !== 'skipped' && (
                            <>
                              <span className="text-success font-medium">✓ {sr?.uploaded}</span>
                              {sr?.failed > 0 && <span className="text-error font-medium">✗ {sr?.failed}</span>}
                            </>
                          )}
                          {sr?.status === 'skipped' && (
                            <span className="text-warning font-medium">Skipped</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error table */}
              {errors?.length > 0 && (
                <div className="border border-error/30 rounded-lg overflow-hidden">
                  <div className="bg-error/5 px-4 py-2 border-b border-error/20 flex items-center justify-between">
                    <p className="text-sm font-semibold text-error">{errors?.length} Error{errors?.length !== 1 ? 's' : ''} Found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      onClick={() => downloadErrorReport(errors, summary)}
                    >
                      Download Report
                    </Button>
                  </div>
                  <div className="overflow-x-auto max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/30 sticky top-0">
                        <tr>
                          {['Sheet', 'Row', 'Column', 'Cell Value', 'Error']?.map((h) => (
                            <th key={h} className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {errors?.map((err, i) => (
                          <tr key={i} className="hover:bg-error/5">
                            <td className="px-3 py-2 font-medium text-foreground max-w-[120px] truncate">{err?.sheet}</td>
                            <td className="px-3 py-2 text-muted-foreground">{err?.row ?? '—'}</td>
                            <td className="px-3 py-2 text-muted-foreground">{err?.column ?? '—'}</td>
                            <td className="px-3 py-2 text-muted-foreground max-w-[100px] truncate">{String(err?.value ?? '—')}</td>
                            <td className="px-3 py-2 text-error max-w-[200px]">{err?.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Success message */}
              {summary?.totalRowsUploaded > 0 && errors?.length === 0 && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-4 flex gap-3">
                  <Icon name="CheckCircle2" size={18} color="var(--color-success)" className="flex-shrink-0" />
                  <p className="text-sm text-success font-medium">
                    All {summary?.totalRowsUploaded} courses uploaded successfully across {summary?.processedSheets} institute{summary?.processedSheets !== 1 ? 's' : ''}!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0 bg-muted/20">
          {result ? (
            <>
              <Button variant="outline" onClick={resetState}>
                Upload Another File
              </Button>
              <Button variant="default" onClick={handleClose}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={uploading}>
                Cancel
              </Button>
              <Button
                variant="default"
                iconName={uploading ? 'Loader2' : 'Upload'}
                iconClassName={uploading ? 'animate-spin' : ''}
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? 'Uploading…' : 'Upload Excel File'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkExcelCourseUploadModal;
